import axios, { baseUrl } from "../axios/axios";
import {
  PharmacistProfileImageRequest,
  PharmacistProfileFileResponse,
  PharmacistProfileRequest,
  PharmacistProfileResponse,
  PharmacistStatsResponse,
  PharmacistWorkingHoursEntry,
} from "../types/pharmacistProfile.types";

// ITAM-2622 adviser-profile service. Every endpoint carries PHARMACIST_USER
// (BF-3), so none of them can produce the 401/403 the shared instance turns
// into a global logout — the shared axios is correct here. The LOGOUT pause is
// the one exception and lives in sessionSideEffects.ts on a bare instance
// (3.7, hazard 2), because that endpoint can legitimately reject.

const PROFILE_ENDPOINT = "/pharmacist_profile";

/**
 * Folder the backend writes profile media into
 * (`PharmacistProfileService.PHARMACIST_PROFILE_IMAGE_FOLDER_PATH`). CREATE
 * generates its own names; UPDATE stores each file under its ORIGINAL filename
 * inside this folder, which is why the client-authored `location` below must
 * name exactly the file it uploads.
 */
export const PHARMACIST_MEDIA_FOLDER = "/pharmacist_profile_images";

export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_VIDEO_BYTES = 50 * 1024 * 1024;
export const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
];
export const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/quicktime"];

/**
 * GET the profile. 200 -> data, 404 -> `null` (no profile row yet: the normal
 * onboarding state, NOT an error), anything else rethrows.
 *
 * This mirrors `getConsultPrefill`: this codebase has no `rejectWithValue`
 * anywhere, and normalizes "expected absence" in the service layer so a reducer
 * can never mistake a network failure for a missing profile.
 */
export const getPharmacistProfile = (
  userId: number,
  signal?: AbortSignal
): Promise<PharmacistProfileResponse | null> => {
  return axios
    .get<PharmacistProfileResponse>(
      `${PROFILE_ENDPOINT}/user_id/${userId}`,
      { signal }
    )
    .then((res) => res.data)
    .catch((error) => {
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    });
};

const multipart = (request: PharmacistProfileRequest, files: File[]) => {
  const formData = new FormData();
  formData.append(
    "pharmacistProfileRequestString",
    JSON.stringify(request)
  );
  files.forEach((file) => {
    formData.append("images", file);
  });
  return formData;
};

/**
 * CREATE. The backend pairs `images[i]` with `files[i]` BY INDEX and overwrites
 * each `location` with a generated name, so the request's image rows must be in
 * the same order as the appended files.
 */
export const createPharmacistProfile = async (
  request: PharmacistProfileRequest,
  files: File[]
): Promise<void> => {
  await axios.post(`${baseUrl}${PROFILE_ENDPOINT}`, multipart(request, files), {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/**
 * UPDATE. Overwrites ALL text fields, so always send the complete set. Media is
 * touched only when `updateImages: true`, and then the row set is rebuilt
 * exactly from `images` — kept media are metadata rows reusing their stored
 * location, new files are uploaded under their own filename.
 */
export const updatePharmacistProfile = async (
  request: PharmacistProfileRequest,
  files: File[]
): Promise<void> => {
  await axios.put(`${baseUrl}${PROFILE_ENDPOINT}`, multipart(request, files), {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/** PUT replaces the WHOLE schedule; `[]` clears it. */
export const updateWorkingHours = async (
  userId: number,
  entries: PharmacistWorkingHoursEntry[]
): Promise<void> => {
  await axios.put(
    `${PROFILE_ENDPOINT}/user_id/${userId}/working_hours`,
    entries
  );
};

/**
 * `available=true` is enroll AND resume (one endpoint, BF-12); `available=false`
 * is pause. Serves activation, resume, and the in-app pause control — but NOT
 * the logout pause (3.7).
 */
export const setPharmacistAvailability = async (
  userId: number,
  available: boolean
): Promise<void> => {
  await axios.put(
    `${PROFILE_ENDPOINT}/user_id/${userId}/availability?available=${available}`
  );
};

/**
 * ITAM-2674: consult-report count and consult-created reservation counts for
 * the adviser. Always 200 (an unknown id yields zeros), so nothing is
 * normalized here; failures reject and the caller owns retry.
 */
export const getPharmacistStats = (
  userId: number,
  signal?: AbortSignal
): Promise<PharmacistStatsResponse> =>
  axios
    .get<PharmacistStatsResponse>(
      `${PROFILE_ENDPOINT}/user_id/${userId}/stats`,
      { signal }
    )
    .then((res) => res.data);

// NOTE: DELETE /pharmacist_profile/user_id/{id}/support exists but is
// deliberately NOT exposed in v1 — it is destructive, and pause covers the
// daily need.

/** A file the pharmacist has picked but not yet saved. */
export interface PendingMediaFile {
  file: File;
  type: "IMAGE" | "VIDEO";
}

export interface ImagesUpdate {
  images: PharmacistProfileImageRequest[];
  files: File[];
}

const uniqueFileName = (userId: number, index: number, name: string): string =>
  `${userId}_${Date.now()}_${index}_${name}`.replace(/\s+/g, "_");

/**
 * Build the `images` array + the files to append for an UPDATE.
 *
 * Kept rows are reproduced verbatim (same stored location) so the rebuild does
 * not lose them. New files are uploaded under a uniquified name and their row
 * points at exactly that name — the backend's update path deletes before it
 * writes into one flat shared folder, so two pharmacists uploading `photo.jpg`
 * would otherwise destroy each other's asset. **Uniquifying is hygiene, not the
 * collision boundary** — that is BG-5, a backend fix. The FE deliberately does
 * not compensate further (decision 20).
 *
 * Target post-BG-5 contract: client `location` ignored, server-generated
 * immutable names echoed back, kept media addressed by row id. Revalidate this
 * recipe against the fixed backend at G-6.
 */
export const buildImagesUpdate = (
  userId: number,
  kept: PharmacistProfileFileResponse[],
  pending: PendingMediaFile[]
): ImagesUpdate => {
  const images: PharmacistProfileImageRequest[] = kept.map((row, index) => ({
    location: row.location,
    thumbnail: null,
    streaming: null,
    type: row.type,
    priority: index,
    isSmall: 0,
  }));

  const files: File[] = [];
  pending.forEach((item, index) => {
    const name = uniqueFileName(userId, index, item.file.name);
    const renamed = new File([item.file], name, { type: item.file.type });
    files.push(renamed);
    images.push({
      location: `${PHARMACIST_MEDIA_FOLDER}/${name}`,
      thumbnail: null,
      streaming: null,
      type: item.type,
      priority: kept.length + index,
      isSmall: 0,
    });
  });

  return { images, files };
};

/**
 * The same row set for a CREATE: `location` is a placeholder the backend
 * overwrites by index, so only the ORDER and the type matter.
 */
export const buildImagesCreate = (
  pending: PendingMediaFile[]
): ImagesUpdate => ({
  images: pending.map((item, index) => ({
    location: null,
    thumbnail: null,
    streaming: null,
    type: item.type,
    priority: index,
    isSmall: 0,
  })),
  files: pending.map((item) => item.file),
});

/** Client pre-validation mirroring the backend's own file checks. */
export const validateMediaFile = (file: File): string | null => {
  if (ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return file.size > MAX_IMAGE_BYTES
      ? "Slika ne sme biti veća od 10 MB."
      : null;
  }
  if (ALLOWED_VIDEO_TYPES.includes(file.type)) {
    return file.size > MAX_VIDEO_BYTES
      ? "Video ne sme biti veći od 50 MB."
      : null;
  }
  return "Dozvoljene su slike (JPEG, PNG, WebP) i video (MP4, MOV).";
};

export const mediaTypeOf = (file: File): "IMAGE" | "VIDEO" =>
  ALLOWED_VIDEO_TYPES.includes(file.type) ? "VIDEO" : "IMAGE";
